// ============================================
// ShaderCache — startup-compiled WGSL registry (Slang migration)
// ============================================
//
// Engine shaders are authored in **Slang** and compiled to WGSL at ENGINE STARTUP (via slang-wasm, driven by
// the splash/loading screen) into this cache. render2d shader consumers read the compiled WGSL by name
// (`getCompiledShader('sprite-lit')`) instead of statically importing a `.wgsl` file — so the runtime stays
// WGSL-string-based (no bundler shader compilation, no prebuild) while the source of truth is `.slang`.
//
// Contract: the host (editor splash / player boot) MUST populate this — via `registerCompiledShader` — before
// any rendering. A miss throws (a programming error: a shader was requested before precompile ran, or its
// `.slang` failed to compile). `render2d` itself never compiles Slang (that's the editor's slang-wasm); it
// only consumes the registered WGSL, keeping the runtime package compiler-free + player-portable.

const cache = new Map<string, string>();

/** Register a compiled WGSL shader under a stable name (e.g. 'sprite-lit', 'blit'). Idempotent (last wins). */
export function registerCompiledShader(name: string, wgsl: string): void {
  cache.set(name, wgsl);
}

/** Register many at once (name → WGSL). */
export function registerCompiledShaders(shaders: Record<string, string>): void {
  for (const [name, wgsl] of Object.entries(shaders)) cache.set(name, wgsl);
}

/** True if a shader has been compiled + registered. */
export function hasCompiledShader(name: string): boolean {
  return cache.has(name);
}

/** Get the compiled WGSL for a shader name. Throws if it wasn't precompiled (startup ordering bug). */
export function getCompiledShader(name: string): string {
  const wgsl = cache.get(name);
  if (wgsl === undefined) {
    throw new Error(
      `ShaderCache: '${name}' not compiled. Registered: [${[...cache.keys()].join(', ')}]`,
    );
  }
  return wgsl;
}

/** Names of all registered shaders (for diagnostics / progress UI). */
export function compiledShaderNames(): string[] {
  return [...cache.keys()];
}

/** Snapshot of every registered shader (name → WGSL) — for persisting to a cross-window/persistent cache. */
export function getAllCompiledShaders(): Record<string, string> {
  return Object.fromEntries(cache);
}

/** Clear the cache (e.g. before a full recompile). */
export function clearCompiledShaders(): void {
  cache.clear();
}
